import React from 'react';
import { Route } from 'react-router';
import ApprovedPartner from 'containers/antifraud/approved-partner';
import {
    useAntiFraudQuery,
    useBorrowerPositionByLoanApplicationIdQuery,
} from 'generated/graphql';
import useNavigation from 'hooks/navigation';
import Loading from 'components/loading';
import { BizonScreenWrapper, BizonCheckoutWrapper, BlankWrapper } from 'components/commons';
import AntiFraud from './context/antifraud-context';
import { useAntiFraudHelper } from './hooks';
import LivenessCheckout from './liveness-checkout';
import LivenessIdentity from './liveness-identity';
import { getLoanApplicationParams } from 'utils/utils';
import { AntifraudSteps, useCompleteAntifraudStep } from 'hooks/complete-antifraud-step';
import WorthyCreditQuestionnaire from './worthy-credit';

type NextScreenResolverType = {
    name: string,
    supports(): boolean,
    resolve(): Promise<void>,
};

const antiFraudPath = "/:loanApplicationId/socio/identificacao";

const AntiFraudPartnerContainer: React.FC = () => {
    const  loanApplicationId  = getLoanApplicationParams();

    const {
        data,
        loading: antifraudLoading,
        refetch,
    } = useAntiFraudQuery({ variables: { loanApplicationId } });

    const antifraudSteps: AntifraudSteps = ["createCompanyInfo", "continueWorkflow"]
    const { handleCompleteAntiFraudStep } = useCompleteAntifraudStep({antifraudSteps});

    const {
        data: borrowerData,
        loading: borrowerPositionLoading,
    } = useBorrowerPositionByLoanApplicationIdQuery({ variables: { loanApplicationId } });

    const {
        livenessIsDraft,
        hasWorthyCreditToAnswer
    } = useAntiFraudHelper(data?.antiFraud, borrowerData?.borrowerPositionByLoanApplicationId);

    const {
        goToPartnerAntiFraudIdentification,
        goToPartnerLivenessStep,
        goToPartnerCreditWorthyStep,
        goToDocumentCheckout,
        goToError,
    } = useNavigation();

    const resolveNextScreen = async () => {
        if (!data.antiFraud || !borrowerData.borrowerPositionByLoanApplicationId) {
            return;
        }

        const resolvers: NextScreenResolverType[] = [
            {
                name: 'partner_liveness_step',
                supports: () => livenessIsDraft(),
                resolve: async () => goToPartnerLivenessStep(),
            },
            {
                name: 'partner_worthy_credit_step',
                supports: () => hasWorthyCreditToAnswer(),
                resolve: async () => goToPartnerCreditWorthyStep(),
            },
        ];

        const resolver = resolvers.find(res => res.supports());
        if (resolver) {
            try {
                await resolver.resolve();
            } catch (error) {

                goToError();
            }
        }
    };

    React.useEffect(() => {
        goToPartnerAntiFraudIdentification();
    }, []);

    React.useEffect(() => {
        if (!antifraudLoading && data?.antiFraud?.isCompleted) {
            goToDocumentCheckout();
        }
    }, [antifraudLoading]);

    if (antifraudLoading || borrowerPositionLoading) {
        return (
            <Loading />
        )
    }

    return (
        <AntiFraud
            antiFraud={data?.antiFraud}
            borrowerPosition={borrowerData.borrowerPositionByLoanApplicationId}
            refetch={refetch}
        >
            <Route
                exact
                path={antiFraudPath}
                render={() => (
                    <BizonScreenWrapper>
                        <ApprovedPartner />
                    </BizonScreenWrapper>
                )}
            />

            <Route
                path={`${antiFraudPath}/status`}
                render={() => (
                    <BizonCheckoutWrapper>
                        <LivenessCheckout nextScreen={resolveNextScreen} />
                    </BizonCheckoutWrapper>
                )}
            />

            {livenessIsDraft() && (
                <>
                    <Route
                        path={`${antiFraudPath}/prova-de-vida`}
                        render={() => (
                            <BlankWrapper>
                                <LivenessIdentity
                                    onComplete={() => hasWorthyCreditToAnswer() ? goToPartnerCreditWorthyStep() : handleCompleteAntiFraudStep()}
                                />
                            </BlankWrapper>
                        )}
                    />
                </>
            )}

            {hasWorthyCreditToAnswer() && (
                <Route
                    path={`${antiFraudPath}/questionario`}
                    render={() => (
                        <BizonScreenWrapper>
                            <WorthyCreditQuestionnaire onComplete={handleCompleteAntiFraudStep} />
                        </BizonScreenWrapper>
                    )}
                />
            )}
        </AntiFraud>
    );
};

export default AntiFraudPartnerContainer;
